/**
 * Modal de edição do próprio cadastro: nome, e-mail, telefone e avatar.
 *
 * O avatar é redimensionado no navegador para um quadrado pequeno antes de virar data URL —
 * não faz sentido subir 4 MB para um selo de 32px, e o backend rejeita imagens grandes.
 */
import * as Dialog from '@radix-ui/react-dialog';
import { Trash2, Upload } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CampoTelefone } from '@/components/CampoTelefone';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/misc';
import { api, type Usuario } from '@/lib/api';

/** Redimensiona a imagem escolhida para um quadrado (cortado ao centro) e devolve um data URL. */
async function comoAvatar(arquivo: File, lado = 160): Promise<string> {
  const url = URL.createObjectURL(arquivo);
  try {
    const img = await new Promise<HTMLImageElement>((ok, erro) => {
      const el = new Image();
      el.onload = () => ok(el);
      el.onerror = erro;
      el.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = lado;
    canvas.height = lado;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('sem canvas');
    const min = Math.min(img.width, img.height);
    const sx = (img.width - min) / 2;
    const sy = (img.height - min) / 2;
    ctx.drawImage(img, sx, sy, min, min, 0, 0, lado, lado);
    return canvas.toDataURL('image/jpeg', 0.82);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function ModalCadastro({
  aberto,
  onAbrir,
  usuario,
  onSalvo,
}: {
  aberto: boolean;
  onAbrir: (v: boolean) => void;
  usuario: Usuario;
  onSalvo: () => Promise<void>;
}) {
  const [nome, setNome] = useState(usuario.nome);
  const [email, setEmail] = useState(usuario.email);
  const [telefone, setTelefone] = useState(usuario.telefone);
  const [avatar, setAvatar] = useState<string | null>(usuario.avatar);
  const [ocupado, setOcupado] = useState(false);
  const arquivoRef = useRef<HTMLInputElement>(null);

  async function escolherArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      setAvatar(await comoAvatar(f));
    } catch {
      toast.error('Não consegui ler essa imagem.');
    }
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setOcupado(true);
    const corpo: Record<string, unknown> = {};
    if (nome.trim() !== usuario.nome) corpo.nome = nome.trim();
    if (email.trim() !== usuario.email) corpo.email = email.trim();
    if (telefone.trim() !== usuario.telefone) corpo.telefone = telefone.trim();
    if (avatar !== usuario.avatar) corpo.avatar = avatar ?? '';
    if (Object.keys(corpo).length === 0) {
      onAbrir(false);
      setOcupado(false);
      return;
    }
    const r = await api.patch<Usuario>('/auth/eu', corpo);
    setOcupado(false);
    if (!r.ok) {
      toast.error(r.erro || 'Não consegui salvar o cadastro.');
      return;
    }
    await onSalvo();
    toast.success('Cadastro atualizado.');
    onAbrir(false);
  }

  const iniciais = nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <Dialog.Root open={aberto} onOpenChange={onAbrir}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-backdrop)] bg-[rgba(13,16,23,.6)] backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[var(--z-modal)] w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-surface p-6 shadow-[var(--shadow-lg)]">
          <Dialog.Title className="tipo-subtitulo text-text">Meu cadastro</Dialog.Title>

          <form onSubmit={salvar} className="mt-5 flex flex-col gap-4">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <span className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-pill bg-surface-2 font-mono text-lg text-text-secondary">
                {avatar ? (
                  <img src={avatar} alt="" className="size-full object-cover" />
                ) : (
                  iniciais || '?'
                )}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secundario"
                  size="sm"
                  onClick={() => arquivoRef.current?.click()}
                >
                  <Upload className="size-4" />
                  Trocar
                </Button>
                {avatar && (
                  <Button type="button" variant="secundario" size="sm" onClick={() => setAvatar(null)}>
                    <Trash2 className="size-4" />
                    Remover
                  </Button>
                )}
                <input
                  ref={arquivoRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={escolherArquivo}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cad-nome">Nome</Label>
              <Input id="cad-nome" value={nome} onChange={(e) => setNome(e.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cad-email">E-mail</Label>
              <Input
                id="cad-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <CampoTelefone rotulo="Telefone" valor={telefone} onChange={setTelefone} id="cad-tel" />

            <div className="mt-2 flex justify-end gap-3">
              <Button type="button" variant="secundario" onClick={() => onAbrir(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primario" disabled={ocupado}>
                {ocupado ? <Spinner /> : 'Salvar'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
